export type MainPanel = 'settings' | 'templates';
const PANEL_STATE_KEY = '__jurisPanel';
interface PanelMarker { version: 1; owner: string; panel: MainPanel; url: string }

interface PanelNavigationOptions {
  history: Pick<History, 'state' | 'pushState' | 'replaceState' | 'back'>;
  href: () => string;
  isMobile: () => boolean;
  isAllowed: () => boolean;
  onChange: (panel: MainPanel | null) => void;
  owner: string;
}

/** A same-URL entry for a main panel; unrelated history entries are never consumed. */
export function createPanelNavigation(options: PanelNavigationOptions) {
  const { history, href, isMobile, isAllowed, onChange } = options;
  const readMarker = (): PanelMarker | null => {
    const value = history.state?.[PANEL_STATE_KEY];
    return value?.version === 1 && typeof value.owner === 'string' && typeof value.url === 'string'
      && (value.panel === 'settings' || value.panel === 'templates') ? value : null;
  };
  const initialMarker = readMarker();
  const owner = initialMarker?.url === href() ? initialMarker.owner : options.owner;
  let visiblePanel: MainPanel | null = null;
  let consuming = false;
  let queuedPanel: MainPanel | null = null;
  let consumingUrl = '';
  let queuedNavigation: (() => void) | null = null;
  let disposed = false;
  const currentMarker = () => {
    const marker = readMarker();
    return marker?.owner === owner && marker.url === href() ? marker : null;
  };
  const publish = (panel: MainPanel | null) => {
    visiblePanel = panel;
    if (!disposed) onChange(panel);
  };
  const consume = (next: MainPanel | null) => {
    queuedPanel = next;
    consumingUrl = href();
    consuming = true;
    history.back();
  };
  const open = (panel: MainPanel) => {
    if (disposed || !isAllowed()) return;
    publish(panel);
    if (consuming) { queuedNavigation = null; queuedPanel = panel; return; }
    if (!isMobile()) return;
    const state = history.state && typeof history.state === 'object' && !Array.isArray(history.state)
      ? history.state : {};
    const nextState = { ...state, [PANEL_STATE_KEY]: { version: 1, owner, panel, url: href() } };
    if (currentMarker()) history.replaceState(nextState, '', href());
    else history.pushState(nextState, '', href());
  };
  const close = () => {
    if (disposed) return;
    publish(null);
    if (consuming) { queuedNavigation = null; queuedPanel = null; return; }
    if (currentMarker()) consume(null);
  };
  const syncFromHistory = () => {
    if (disposed) return;
    if (consuming) {
      consuming = false;
      const next = queuedPanel;
      const navigation = queuedNavigation;
      queuedPanel = null;
      queuedNavigation = null;
      if (navigation && href() === consumingUrl) { publish(null); navigation(); return; }
      if (next && isAllowed() && href() === consumingUrl) open(next);
      else publish(null);
      return;
    }
    const marker = currentMarker();
    publish(isAllowed() && marker ? marker.panel : null);
  };
  const refresh = () => {
    if (!isAllowed()) { if (visiblePanel) close(); return; }
    if (consuming) return;
    const marker = currentMarker();
    if (marker) publish(marker.panel);
    else if (visiblePanel && isMobile()) open(visiblePanel);
  };
  const beforeNavigate = (navigation: () => void) => {
    if (disposed) return;
    publish(null);
    if (consuming) { queuedPanel = null; queuedNavigation = navigation; return; }
    if (currentMarker()) { queuedNavigation = navigation; consume(null); }
    else navigation();
  };
  return { open, close, syncFromHistory, refresh, beforeNavigate, dispose: () => { disposed = true; } };
}
